import { RequestsPanel } from "@/components/RequestsPanel";
import { DeletedMessagesVault } from "@/components/DeletedMessagesVault";
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { MediaDownloadDialog } from "@/components/MediaDownloadDialog";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Loader2, Building2, Users, DoorClosed, DollarSign, LogOut, Sparkles, MapPin, Settings2, Power, PowerOff, Percent, AlertTriangle, CheckCircle2, Clock, Trash2, FileText, FlaskConical, Eye, BookOpen, Clapperboard } from "lucide-react";

export default function SuperAdmin() {
  const { logout, impersonate, acceptTerms } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [managingId, setManagingId] = useState(null);
  const [discountFor, setDiscountFor] = useState(null);
  const [media, setMedia] = useState(null);
  const [discForm, setDiscForm] = useState({ percent: "10", months: "" });
  const [pricingOpen, setPricingOpen] = useState(false);
  const [priceForm, setPriceForm] = useState({ rate_cleaner: "0.5", rate_room: "0.5", volume_rate: "0.15", tier1_cleaners: "10", tier1_rooms: "25" });
  const [termsOpen, setTermsOpen] = useState(false);
  const [termsForm, setTermsForm] = useState({ version: "", effective_date: "", intro: "", sections: [] });
  const [deleteFor, setDeleteFor] = useState(null);
  const [editFor, setEditFor] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", location: "" });
  const [form, setForm] = useState({
    name: "",
    location: "Hamilton, New Zealand",
    owner_name: "",
    owner_email: "",
    owner_password: "",
  });

  const { data: stats } = useQuery({
    queryKey: ["admin-stats"],
    queryFn: async () => (await api.get("/admin/stats")).data,
  });
  const { data: motels = [], isLoading } = useQuery({
    queryKey: ["motels"],
    queryFn: async () => (await api.get("/motels")).data,
  });
  const { data: pricing } = useQuery({
    queryKey: ["admin-pricing"],
    queryFn: async () => (await api.get("/admin/pricing")).data,
  });
  const savePricing = useMutation({
    mutationFn: (body) => api.put("/admin/pricing", body),
    onSuccess: () => {
      toast.success("Pricing updated");
      setPricingOpen(false);
      qc.invalidateQueries({ queryKey: ["admin-pricing"] });
      qc.invalidateQueries({ queryKey: ["motels"] });
      qc.invalidateQueries({ queryKey: ["admin-stats"] });
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const { data: terms } = useQuery({
    queryKey: ["admin-terms"],
    queryFn: async () => (await api.get("/terms")).data,
  });
  const saveTerms = useMutation({
    mutationFn: (body) => api.put("/admin/terms", body),
    onSuccess: async () => {
      toast.success("Terms & Conditions updated");
      setTermsOpen(false);
      qc.invalidateQueries({ queryKey: ["admin-terms"] });
      // The admin who just edited the Terms implicitly accepts them, so a version
      // bump doesn't immediately lock them out of their own screen.
      try { await acceptTerms(); } catch { /* non-blocking */ }
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const openTerms = () => {
    setTermsForm({
      version: terms?.version || "1.0",
      effective_date: terms?.effective_date || "",
      intro: terms?.intro || "",
      sections: (terms?.sections || []).map((s) => ({ ...s, _k: crypto.randomUUID() })),
    });
    setTermsOpen(true);
  };
  const setSection = (i, key, val) =>
    setTermsForm((f) => ({ ...f, sections: f.sections.map((s, idx) => (idx === i ? { ...s, [key]: val } : s)) }));
  const addSection = () => setTermsForm((f) => ({ ...f, sections: [...f.sections, { title: "", body: "", _k: crypto.randomUUID() }] }));
  const removeSection = (i) => setTermsForm((f) => ({ ...f, sections: f.sections.filter((_, idx) => idx !== i) }));

  const create = useMutation({
    mutationFn: () => api.post("/motels", form),
    onSuccess: () => {
      toast.success("Motel created");
      setOpen(false);
      setForm({ name: "", location: "Hamilton, New Zealand", owner_name: "", owner_email: "", owner_password: "" });
      qc.invalidateQueries({ queryKey: ["motels"] });
      qc.invalidateQueries({ queryKey: ["admin-stats"] });
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const setAccess = useMutation({
    mutationFn: ({ id, disabled }) => api.post(`/motels/${id}/access`, { disabled }),
    onSuccess: (_r, v) => { toast.success(v.disabled ? "Motel disabled (read-only)" : "Motel enabled"); qc.invalidateQueries({ queryKey: ["motels"] }); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const editMotel = useMutation({
    mutationFn: ({ id, name, location }) => api.patch(`/motels/${id}`, { name, location }),
    onSuccess: (r) => {
      toast.success(`Saved — Property ID ${r.data.property_code}`);
      setEditFor(null);
      qc.invalidateQueries({ queryKey: ["motels"] });
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const setPastDue = useMutation({
    mutationFn: ({ id, past_due }) => api.post(`/motels/${id}/mark-past-due`, { past_due }),
    onSuccess: (_r, v) => { toast.success(v.past_due ? "Marked past due (7-day countdown started)" : "Cleared past due"); qc.invalidateQueries({ queryKey: ["motels"] }); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const setDiscount = useMutation({
    mutationFn: ({ id, percent, months }) => api.post(`/motels/${id}/discount`, { percent, months }),
    onSuccess: () => {
      toast.success("Discount saved");
      setDiscountFor(null);
      qc.invalidateQueries({ queryKey: ["motels"] });
      qc.invalidateQueries({ queryKey: ["admin-stats"] });
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const deleteMotel = useMutation({
    mutationFn: (id) => api.delete(`/motels/${id}`),
    onSuccess: () => {
      toast.success("Motel deleted");
      setDeleteFor(null);
      qc.invalidateQueries({ queryKey: ["motels"] });
      qc.invalidateQueries({ queryKey: ["admin-stats"] });
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const manage = async (m, ghost = false) => {
    setManagingId(m.id);
    try {
      await impersonate(m.id, ghost);
      toast.success(ghost ? `Viewing ${m.name} (read-only)` : `Managing ${m.name}`);
      navigate("/manager");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setManagingId(null);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <div className="flex items-center gap-2.5">
            <div className="flex h-9 w-9 items-center justify-center rounded-md bg-primary">
              <Sparkles className="h-5 w-5 text-primary-foreground" />
            </div>
            <div>
              <div className="font-display text-lg font-extrabold leading-none">KlenaPro</div>
              <div className="label-caps text-muted-foreground">Super Admin</div>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={() => { logout(); navigate("/login"); }} data-testid="admin-logout" className="gap-2">
            <LogOut className="h-4 w-4" /> Sign out
          </Button>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-6 py-8">
        <section className="mb-8" data-testid="admin-requests-section">
          <h2 className="font-display text-xl font-extrabold">Customer requests &amp; feedback</h2>
          <p className="mb-3 mt-1 text-sm text-muted-foreground">Feature requests, change requests and bug reports from every property, with their attachments. Reply, set a status and leave a developer note.</p>
          <RequestsPanel isAdmin />
        </section>
        <DeletedMessagesVault />

        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="font-display text-3xl font-extrabold">Motel Accounts</h1>
            <p className="mt-1 text-sm text-muted-foreground">Manage all tenant motels and billing.</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => { setPriceForm({ rate_cleaner: String(pricing?.rate_cleaner ?? "0.5"), rate_room: String(pricing?.rate_room ?? "0.5"), volume_rate: String(pricing?.volume_rate ?? "0.15"), tier1_cleaners: String(pricing?.tier1_cleaners ?? "10"), tier1_rooms: String(pricing?.tier1_rooms ?? "25") }); setPricingOpen(true); }} data-testid="pricing-btn" className="k-press gap-2">
              <DollarSign className="h-4 w-4" /> Pricing
            </Button>
            <Button variant="outline" onClick={() => setMedia({ path: "/admin/product-guide/pdf", filename: "KlenaPro_Product_Guide.pdf", title: "Product guide (PDF)", mime: "application/pdf", shareKey: "pdf" })} data-testid="product-guide-btn" className="k-press gap-2">
              <BookOpen className="h-4 w-4" /> Product guide
            </Button>
            <Button variant="outline" onClick={() => setMedia({ path: "/admin/marketing/video", filename: "KlenaPro_sales_video_16x9.mp4", title: "Sales video 16:9", playable: true, shareKey: "landscape" })} data-testid="sales-video-btn" className="k-press gap-2">
              <Clapperboard className="h-4 w-4" /> Video 16:9
            </Button>
            <Button variant="outline" onClick={() => setMedia({ path: "/admin/marketing/video?variant=vertical", filename: "KlenaPro_sales_video_9x16.mp4", title: "Sales video 9:16", playable: true, shareKey: "vertical" })} data-testid="sales-video-vertical-btn" className="k-press gap-2">
              <Clapperboard className="h-4 w-4" /> Video 9:16
            </Button>
            <Button variant="outline" onClick={() => setMedia({ path: "/admin/marketing/video?variant=guide", filename: "KlenaPro_Product_Guide_video.mp4", title: "Product guide video (~8 min)", playable: true, shareKey: "guide" })} data-testid="guide-video-btn" className="k-press gap-2">
              <Clapperboard className="h-4 w-4" /> Guide video
            </Button>
            <Button variant="outline" onClick={openTerms} data-testid="edit-terms-btn" className="k-press gap-2">
              <FileText className="h-4 w-4" /> Edit T&amp;C
            </Button>
            <Button onClick={() => setOpen(true)} data-testid="add-motel-btn" className="k-press gap-2">
              <Plus className="h-4 w-4" /> New Motel
            </Button>
          </div>
        </div>

        <div className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-4">
          <Stat icon={Building2} label="Motels" value={stats?.motels ?? "—"} />
          <Stat icon={Users} label="Cleaners" value={stats?.cleaners ?? "—"} />
          <Stat icon={DoorClosed} label="Rooms" value={stats?.rooms ?? "—"} />
          <Stat
            icon={DollarSign}
            label="Est. Monthly Revenue"
            value={stats ? `$${stats.monthly_revenue}` : "—"}
            sub={stats ? (stats.monthly_discount_impact > 0
              ? `List $${stats.monthly_revenue_list} · saving $${stats.monthly_discount_impact}`
              : `List $${stats.monthly_revenue_list} · no discounts`) : null}
            subTestid="revenue-discount-split"
          />
        </div>

        {isLoading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3" data-testid="motels-grid">
            {motels.map((m) => (
              <Card
                key={m.id}
                data-testid={`motel-card-${m.id}`}
                className={`relative overflow-hidden rounded-md border p-5 ${
                  m.is_demo ? "border-amber-300 bg-amber-50/70 ring-1 ring-amber-200" : "border-border"
                }`}
              >
                {m.is_demo && (
                  <>
                    <span
                      aria-hidden="true"
                      className="pointer-events-none absolute -right-8 top-4 rotate-45 select-none text-[38px] font-extrabold uppercase tracking-widest text-amber-500/15"
                    >
                      Demo
                    </span>
                    <span
                      data-testid={`demo-badge-${m.id}`}
                      className="absolute right-3 top-3 z-10 inline-flex items-center gap-1 rounded-full border border-amber-400 bg-amber-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-800"
                    >
                      <FlaskConical className="h-3 w-3" /> Demo
                    </span>
                  </>
                )}
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <Building2 className="h-5 w-5 text-muted-foreground" />
                    <span className="font-display text-lg font-bold">{m.name}</span>
                  </div>
                  {!m.is_demo && <AccessBadge access={m.access} />}
                </div>
                <div className="mt-1 flex items-center gap-1 text-xs text-muted-foreground">
                  <MapPin className="h-3 w-3" /> {m.location}
                </div>
                <div className="mt-1.5 flex items-center gap-2" data-testid={`property-code-${m.id}`}>
                  <span className="label-caps text-muted-foreground">Property ID</span>
                  <span className="rounded bg-muted px-2 py-0.5 font-mono text-sm font-bold tracking-wider">{m.property_code || "—"}</span>
                </div>
                <div className="mt-4 grid grid-cols-3 gap-2 text-center">
                  <MiniStat label="Cleaners" value={m.cleaner_count} />
                  <MiniStat label="Rooms" value={m.room_count} />
                  <MiniStat
                    label="Est/mo"
                    value={`$${m.discount ? m.monthly_cost_effective : m.monthly_cost}`}
                    testid={`monthly-cost-${m.id}`}
                  />
                </div>
                {m.discount && (
                  <div className="mt-2 flex items-center justify-center gap-1 text-xs font-semibold text-emerald-700" data-testid={`discount-badge-${m.id}`}>
                    <Percent className="h-3 w-3" /> {m.discount.percent}% off{" "}
                    {m.discount.until
                      ? `until ${m.discount.until.slice(0, 10)} · ${discountEnds(m.discount.until)}`
                      : "(ongoing)"}
                  </div>
                )}
                <div className="mt-4 grid grid-cols-2 gap-2">
                  <Button
                    onClick={() => manage(m, true)}
                    disabled={managingId === m.id}
                    data-testid={`ghost-motel-${m.id}`}
                    variant="outline"
                    className="k-press gap-2"
                    title="Open read-only — great for bug hunting / research"
                  >
                    {managingId === m.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />}
                    Ghost view
                  </Button>
                  <Button
                    onClick={() => manage(m, false)}
                    disabled={managingId === m.id}
                    data-testid={`manage-motel-${m.id}`}
                    variant="outline"
                    className="k-press gap-2"
                    title="Full access — you can edit this motel's data"
                  >
                    {managingId === m.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Settings2 className="h-4 w-4" />}
                    Full access
                  </Button>
                </div>
                <div className="mt-2 grid grid-cols-2 gap-2">
                  {m.access?.reason === "disabled" ? (
                    <Button
                      variant="outline"
                      onClick={() => setAccess.mutate({ id: m.id, disabled: false })}
                      data-testid={`enable-motel-${m.id}`}
                      className="k-press gap-1.5 border-emerald-300 text-xs font-semibold text-emerald-700 hover:bg-emerald-50"
                    >
                      <Power className="h-3.5 w-3.5" /> Enable
                    </Button>
                  ) : (
                    <Button
                      variant="outline"
                      onClick={() => setAccess.mutate({ id: m.id, disabled: true })}
                      data-testid={`disable-motel-${m.id}`}
                      className="k-press gap-1.5 border-destructive/40 text-xs font-semibold text-destructive hover:bg-destructive/10"
                    >
                      <PowerOff className="h-3.5 w-3.5" /> Disable
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    onClick={() => { setEditForm({ name: m.name, location: m.location || "" }); setEditFor(m); }}
                    data-testid={`edit-motel-${m.id}`}
                    className="k-press gap-1.5 text-xs font-semibold"
                  >
                    <Settings2 className="h-3.5 w-3.5" /> Edit
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => { setDiscForm({ percent: String(m.discount?.percent || "10"), months: "" }); setDiscountFor(m); }}
                    data-testid={`discount-motel-${m.id}`}
                    className="k-press gap-1.5 text-xs font-semibold"
                  >
                    <Percent className="h-3.5 w-3.5" /> Discount
                  </Button>
                </div>
                <button
                  type="button"
                  onClick={() => setPastDue.mutate({ id: m.id, past_due: m.access?.reason !== "grace" && m.access?.reason !== "nonpayment" })}
                  data-testid={`pastdue-motel-${m.id}`}
                  className="k-press mt-2 w-full text-[11px] font-medium text-muted-foreground underline hover:text-foreground"
                >
                  {m.access?.reason === "grace" || m.access?.reason === "nonpayment" ? "Clear past-due (test)" : "Mark past-due (test)"}
                </button>
                {m.deletable ? (
                  <Button
                    variant="ghost"
                    onClick={() => setDeleteFor(m)}
                    data-testid={`delete-motel-${m.id}`}
                    className="k-press mt-1 w-full gap-1.5 text-xs font-semibold text-destructive hover:bg-destructive/10"
                  >
                    <Trash2 className="h-3.5 w-3.5" /> Delete motel
                  </Button>
                ) : (
                  <p className="mt-2 text-center text-[11px] text-muted-foreground" data-testid={`delete-blocked-${m.id}`}>
                    Has data — disable instead of deleting
                  </p>
                )}
              </Card>
            ))}
          </div>
        )}
      </main>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="add-motel-dialog">
          <DialogHeader>
            <DialogTitle>New Motel Account</DialogTitle>
            <DialogDescription>Provision an isolated motel tenant with its own owner login.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Field label="Motel name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="motel-name-input" placeholder="Seaside Motor Lodge" />
            <Field label="Location" value={form.location} onChange={(v) => setForm({ ...form, location: v })} testid="motel-location-input" />
            <Field label="Owner name" value={form.owner_name} onChange={(v) => setForm({ ...form, owner_name: v })} testid="owner-name-input" placeholder="Owner full name" />
            <Field label="Owner email" value={form.owner_email} onChange={(v) => setForm({ ...form, owner_email: v })} testid="owner-email-input" placeholder="owner@motel.co.nz" />
            <Field label="Owner password" value={form.owner_password} onChange={(v) => setForm({ ...form, owner_password: v })} testid="owner-password-input" placeholder="Temporary password" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button
              onClick={() => create.mutate()}
              disabled={!form.name || !form.owner_email || !form.owner_name || !form.owner_password || create.isPending}
              data-testid="save-motel-btn"
              className="gap-2"
            >
              {create.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              Create motel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteFor} onOpenChange={(v) => !v && setDeleteFor(null)}>
        <DialogContent data-testid="delete-motel-dialog">
          <DialogHeader>
            <DialogTitle>Delete {deleteFor?.name}?</DialogTitle>
            <DialogDescription>This permanently removes the empty motel and its owner/manager login. This cannot be undone.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteFor(null)}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={() => deleteMotel.mutate(deleteFor.id)}
              disabled={deleteMotel.isPending}
              data-testid="delete-motel-confirm"
              className="gap-2"
            >
              {deleteMotel.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              Delete permanently
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={pricingOpen} onOpenChange={setPricingOpen}>
        <DialogContent data-testid="pricing-dialog">
          <DialogHeader>
            <DialogTitle>Subscription pricing</DialogTitle>
            <DialogDescription>Set the standard weekly rate per cleaner and per room. This applies only to NEW subscriptions and to motels that resubscribe after a full lapse. Existing active subscriptions keep their locked-in rate.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Field label="$ per cleaner / week" value={priceForm.rate_cleaner} onChange={(v) => setPriceForm({ ...priceForm, rate_cleaner: v })} testid="rate-cleaner-input" placeholder="0.50" />
            <Field label="$ per room / week" value={priceForm.rate_room} onChange={(v) => setPriceForm({ ...priceForm, rate_room: v })} testid="rate-room-input" placeholder="0.50" />
            <div className="rounded-md border p-3" data-testid="volume-pricing-block">
              <div className="label-caps mb-2 text-muted-foreground">Volume pricing</div>
              <div className="grid grid-cols-3 gap-2">
                <Field label="Base rate covers first … cleaners" value={priceForm.tier1_cleaners} onChange={(v) => setPriceForm({ ...priceForm, tier1_cleaners: v })} testid="tier1-cleaners-input" placeholder="10" />
                <Field label="… and first … rooms" value={priceForm.tier1_rooms} onChange={(v) => setPriceForm({ ...priceForm, tier1_rooms: v })} testid="tier1-rooms-input" placeholder="25" />
                <Field label="$ / week beyond those" value={priceForm.volume_rate} onChange={(v) => setPriceForm({ ...priceForm, volume_rate: v })} testid="volume-rate-input" placeholder="0.15" />
              </div>
            </div>
            <p className="rounded-md bg-muted/60 p-2.5 text-xs text-muted-foreground" data-testid="pricing-lock-note">
              Prices are locked when a paid subscription starts and stay fixed while it remains valid. Changing these rates will not affect any motel with an active subscription.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPricingOpen(false)}>Cancel</Button>
            <Button
              onClick={() => savePricing.mutate({
                rate_cleaner: parseFloat(priceForm.rate_cleaner) || 0, rate_room: parseFloat(priceForm.rate_room) || 0,
                volume_rate: parseFloat(priceForm.volume_rate) || 0,
                tier1_cleaners: parseInt(priceForm.tier1_cleaners, 10) || 0, tier1_rooms: parseInt(priceForm.tier1_rooms, 10) || 0,
              })}
              disabled={savePricing.isPending}
              data-testid="pricing-save-btn"
              className="gap-2"
            >
              {savePricing.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              Save pricing
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={termsOpen} onOpenChange={setTermsOpen}>
        <DialogContent data-testid="terms-dialog" className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Terms &amp; Conditions</DialogTitle>
            <DialogDescription>Edit the Terms shown at signup and on the /terms page. Bumping the version re-prompts every user to accept the new Terms at their next action.</DialogDescription>
          </DialogHeader>
          <div className="max-h-[58vh] space-y-4 overflow-y-auto pr-1">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Version</Label>
                <Input data-testid="terms-version-input" value={termsForm.version} onChange={(e) => setTermsForm({ ...termsForm, version: e.target.value })} placeholder="1.1" />
              </div>
              <div className="space-y-1.5">
                <Label>Effective date</Label>
                <Input data-testid="terms-effective-input" value={termsForm.effective_date} onChange={(e) => setTermsForm({ ...termsForm, effective_date: e.target.value })} placeholder="2026-06-01" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Introduction</Label>
              <Textarea data-testid="terms-intro-input" rows={4} value={termsForm.intro} onChange={(e) => setTermsForm({ ...termsForm, intro: e.target.value })} />
            </div>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Sections</Label>
                <Button type="button" size="sm" variant="outline" onClick={addSection} data-testid="terms-add-section" className="gap-1.5">
                  <Plus className="h-3.5 w-3.5" /> Add section
                </Button>
              </div>
              {termsForm.sections.map((s, i) => (
                <div key={s._k || i} className="space-y-2 rounded-md border p-3" data-testid={`terms-section-${i}`}>
                  <div className="flex items-center gap-2">
                    <Input data-testid={`terms-section-title-${i}`} value={s.title} onChange={(e) => setSection(i, "title", e.target.value)} placeholder="Section title" />
                    <Button type="button" size="icon" variant="ghost" onClick={() => removeSection(i)} data-testid={`terms-section-remove-${i}`}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                  <Textarea data-testid={`terms-section-body-${i}`} rows={4} value={s.body} onChange={(e) => setSection(i, "body", e.target.value)} placeholder="Section text" />
                </div>
              ))}
              {termsForm.sections.length === 0 && (
                <p className="text-sm text-muted-foreground">No sections yet — add one above.</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTermsOpen(false)}>Cancel</Button>
            <Button
              onClick={() => saveTerms.mutate({
                version: termsForm.version.trim(),
                effective_date: termsForm.effective_date.trim(),
                intro: termsForm.intro,
                sections: termsForm.sections.filter((s) => s.title.trim()).map((s) => ({ title: s.title, body: s.body })),
              })}
              disabled={saveTerms.isPending || !termsForm.version.trim() || termsForm.sections.filter((s) => s.title.trim()).length === 0}
              data-testid="terms-save-btn"
              className="gap-2"
            >
              {saveTerms.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              Save Terms
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editFor} onOpenChange={(v) => !v && setEditFor(null)}>
        <DialogContent data-testid="edit-motel-dialog">
          <DialogHeader>
            <DialogTitle>Edit motel account</DialogTitle>
            <DialogDescription>
              Changing the name issues a new Property ID (staff will use the new one to sign in).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Field label="Motel name" value={editForm.name} onChange={(v) => setEditForm({ ...editForm, name: v })} testid="edit-motel-name-input" />
            <Field label="Location" value={editForm.location} onChange={(v) => setEditForm({ ...editForm, location: v })} testid="edit-motel-location-input" />
            {editFor?.property_code && (
              <p className="text-xs text-muted-foreground">
                Current Property ID: <span className="font-mono font-bold">{editFor.property_code}</span>
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditFor(null)}>Cancel</Button>
            <Button
              onClick={() => editMotel.mutate({ id: editFor.id, name: editForm.name, location: editForm.location })}
              disabled={!editForm.name || editMotel.isPending}
              data-testid="edit-motel-save-btn"
              className="gap-2"
            >
              {editMotel.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              Save changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!discountFor} onOpenChange={(v) => !v && setDiscountFor(null)}>
        <DialogContent data-testid="discount-dialog">
          <DialogHeader>
            <DialogTitle>Discount — {discountFor?.name}</DialogTitle>
            <DialogDescription>Apply a percentage discount for a set number of months, or leave months blank for ongoing.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Field label="Discount %" value={discForm.percent} onChange={(v) => setDiscForm({ ...discForm, percent: v })} testid="discount-percent-input" placeholder="10" />
            <Field label="Number of months (blank = ongoing)" value={discForm.months} onChange={(v) => setDiscForm({ ...discForm, months: v })} testid="discount-months-input" placeholder="e.g. 3" />
          </div>
          <DialogFooter>
            {discountFor?.discount && (
              <Button
                variant="ghost"
                onClick={() => setDiscount.mutate({ id: discountFor.id, percent: 0, months: null })}
                data-testid="discount-remove-btn"
                className="mr-auto text-destructive"
              >
                Remove discount
              </Button>
            )}
            <Button variant="outline" onClick={() => setDiscountFor(null)}>Cancel</Button>
            <Button
              onClick={() => setDiscount.mutate({
                id: discountFor.id,
                percent: parseFloat(discForm.percent) || 0,
                months: discForm.months ? parseInt(discForm.months, 10) : null,
              })}
              disabled={setDiscount.isPending}
              data-testid="discount-save-btn"
              className="gap-2"
            >
              {setDiscount.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              Save discount
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <MediaDownloadDialog item={media} onClose={() => setMedia(null)} />
    </div>
  );
}

function AccessBadge({ access }) {
  if (!access) return null;
  const map = {
    disabled: { icon: PowerOff, cls: "bg-destructive/10 text-destructive", label: "Disabled" },
    nonpayment: { icon: PowerOff, cls: "bg-destructive/10 text-destructive", label: "Locked · unpaid" },
    grace: { icon: Clock, cls: "bg-amber-100 text-amber-700", label: `Grace · ${access.days_left}d` },
    active: { icon: CheckCircle2, cls: "bg-emerald-100 text-emerald-700", label: "Active" },
  };
  const s = map[access.reason] || map.active;
  const Icon = s.icon;
  return (
    <span data-testid="motel-access-badge" className={`inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${s.cls}`}>
      <Icon className="h-3 w-3" /> {s.label}
    </span>
  );
}

function discountEnds(iso) {
  const ms = new Date(iso) - new Date();
  if (ms <= 0) return "expired";
  const days = Math.ceil(ms / 86400000);
  return days === 1 ? "ends tomorrow" : `ends in ${days} days`;
}

function Stat({ icon: Icon, label, value, sub, subTestid }) {
  return (
    <Card className="rounded-md border p-4">
      <div className="flex items-center gap-2 text-muted-foreground">
        <Icon className="h-4 w-4" />
        <span className="label-caps">{label}</span>
      </div>
      <div className="mt-2 font-display text-2xl font-extrabold">{value}</div>
      {sub && (
        <div className="mt-0.5 text-xs text-muted-foreground" data-testid={subTestid}>{sub}</div>
      )}
    </Card>
  );
}

function MiniStat({ label, value, testid }) {
  return (
    <div className="rounded-md bg-secondary/50 py-2" data-testid={testid}>
      <div className="font-display text-lg font-bold">{value}</div>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
    </div>
  );
}

function Field({ label, value, onChange, testid, placeholder }) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <Input value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} placeholder={placeholder} />
    </div>
  );
}
